import {
  type AnyPgColumn,
  check,
  doublePrecision,
  foreignKey,
  index,
  integer,
  pgTable,
  primaryKey,
  text,
  uuid,
  unique,
  jsonb,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { id, sortIndex, temporalInstant, temporalTimestamps, allOrNone } from "../../utils/sql";
import { unit } from "../../common/unit/unit.sql";
import { relations } from "drizzle-orm";
import type { Nutrient } from "../nutrient";
import type { Unit } from "@corpuscomplete/core/common/unit";
import { foodCollection } from "../food-collection/food-collection.sql";
import { visibility, discoverability } from "../../common/utils.sql";
import { type FoodModifier, type ServingModifier } from "./constants";
import { foodCategory, foodCategoryAssociationType } from "../food-category/food-category.sql";
import { foodType } from "./food-type.sql";

export const foodMeta = pgTable(
  "food_meta",
  {
    ...id,
    ...temporalTimestamps,
    type: foodType("type").notNull(),
    collectionId: uuid("collection_id")
      .references(() => foodCollection.id)
      .notNull(),
    currentVersion: integer("current_version"),
    visibility: visibility("visibility").notNull(),
    discoverability: discoverability("discoverability").notNull(),
  },
  (t) => [
    foreignKey({
      name: "food_meta_current_food_fk",
      columns: [t.id, t.currentVersion],
      foreignColumns: [foodForMetaFk().id, foodForMetaFk().version],
    }),
    unique("food_meta_unique").on(t.collectionId, t.id),
  ],
);

// Needed to avoid circular dependency style Typescript errors
const foodForMetaFk = (): { id: AnyPgColumn; version: AnyPgColumn } => food;

export const foodMetaRelations = relations(foodMeta, ({ one }) => ({
  collection: one(foodCollection, {
    fields: [foodMeta.collectionId],
    references: [foodCollection.id],
  }),
  current: one(food, {
    fields: [foodMeta.id, foodMeta.currentVersion],
    references: [food.id, food.version],
  }),
}));

export const food = pgTable(
  "food",
  {
    id: uuid("id")
      .notNull()
      .references(() => foodMeta.id),
    version: integer("version").notNull(),
    timeCreated: temporalInstant("time_created").notNull(),
    name: text("name").notNull(),
    modifiers: text("modifiers").array().notNull().$type<FoodModifier[]>(),
    iconSlugs: text("icon_slugs").array(),
  },
  (t) => [
    primaryKey({ name: "food_pk", columns: [t.id, t.version] }),
    index("food_name_trgm_idx").using(
      "gin",
      sql.raw(
        `("name" || COALESCE(', ' || immutable_array_to_string("modifiers", ', '), '')) gin_trgm_ops`,
      ),
    ),
    check("icon_slugs_max_4", sql`array_length(icon_slugs, 1) <= 4`),
    check("icon_slugs_min_1", sql`icon_slugs IS NULL OR array_length(icon_slugs, 1) >= 1`),
  ],
);

export const foodRelations = relations(food, ({ many, one }) => ({
  servings: many(foodServing),
  categoryAssociations: many(foodCategoryAssociation),
  meta: one(foodMeta, {
    fields: [food.id],
    references: [foodMeta.id],
  }),
  metaIfCurrent: one(foodMeta, {
    fields: [food.id, food.version],
    references: [foodMeta.id, foodMeta.currentVersion],
  }),
}));

export const foodServing = pgTable(
  "food_serving",
  {
    ...id,
    ...sortIndex,
    foodId: uuid("food_id").notNull(),
    foodVersion: integer("food_version").notNull(),
    name: text("name").notNull(),
    modifiers: text("modifiers").array().notNull().$type<ServingModifier[]>(),
    defaultAmount: doublePrecision("default_amount").notNull(),
    massQuantity: doublePrecision("mass_quantity"),
    massUnitKey: text("mass_unit_key")
      .references(() => unit.key)
      .$type<Unit.Key<"mass">>(),
    volumeQuantity: doublePrecision("volume_quantity"),
    volumeUnitKey: text("volume_unit_key")
      .references(() => unit.key)
      .$type<Unit.Key<"volume">>(),
    nutrients: jsonb("nutrients").$type<Nutrient.NormalizedAmounts>().notNull(),
  },
  (t) => [
    unique("food_serving_unique").on(t.foodId, t.id),
    unique("food_serving_unique_sort_index").on(t.foodId, t.foodVersion, t.sortIndex),
    foreignKey({
      name: "food_serving_food_fk",
      columns: [t.foodId, t.foodVersion],
      foreignColumns: [food.id, food.version],
    }),
    allOrNone("food_serving_mass", [t.massQuantity, t.massUnitKey]),
    allOrNone("food_serving_volume", [t.volumeQuantity, t.volumeUnitKey]),
  ],
);

export const foodServingRelations = relations(foodServing, ({ one }) => ({
  food: one(food, {
    fields: [foodServing.foodId, foodServing.foodVersion],
    references: [food.id, food.version],
  }),
  foodMeta: one(foodMeta, {
    fields: [foodServing.foodId],
    references: [foodMeta.id],
  }),
  massUnit: one(unit, {
    fields: [foodServing.massUnitKey],
    references: [unit.key],
  }),
  volumeUnit: one(unit, {
    fields: [foodServing.volumeUnitKey],
    references: [unit.key],
  }),
}));

export const foodCategoryAssociation = pgTable(
  "food_category_association",
  {
    foodId: uuid("food_id").notNull(),
    foodVersion: integer("food_version").notNull(),
    categoryId: uuid("category_id")
      .notNull()
      .references(() => foodCategory.id),
    type: foodCategoryAssociationType("type").notNull(),
  },
  (t) => [
    primaryKey({
      name: "food_category_association_pk",
      columns: [t.foodId, t.foodVersion, t.categoryId],
    }),
    foreignKey({
      name: "food_category_association_food_fk",
      columns: [t.foodId, t.foodVersion],
      foreignColumns: [food.id, food.version],
    }),
    index("food_category_association_food_idx").on(t.foodId, t.foodVersion),
    index("food_category_association_category_idx").on(t.categoryId),
  ],
);

export const foodCategoryAssociationRelations = relations(foodCategoryAssociation, ({ one }) => ({
  food: one(food, {
    fields: [foodCategoryAssociation.foodId, foodCategoryAssociation.foodVersion],
    references: [food.id, food.version],
  }),
  category: one(foodCategory, {
    fields: [foodCategoryAssociation.categoryId],
    references: [foodCategory.id],
  }),
}));
